import React, { Component, createContext, type ReactNode, useContext, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { type ThemeColorModes, type ThemeState } from '@atlaskit/tokens';

import { DEFAULT_THEME } from '../constants';
import { updateThemeWithDefaultValues } from '../utils';

import type { globalThemeProviderQuery as GlobalThemeProviderQueryType } from './__generated__/globalThemeProviderQuery.graphql';

type GlobalThemeContextValue = {
	theme: Partial<ThemeState> | undefined;
};

const GlobalThemeContext = createContext<GlobalThemeContextValue | undefined>(undefined);

export type GlobalThemeProviderProps = {
	/**
	 * The user's account ID to fetch theme preferences for. Can be not set if the user is anonymous.
	 */
	accountId: string | null | undefined;
	/**
	 * Children components that will have access to the global theme via useGlobalTheme hook.
	 */
	children: ReactNode;
};

/**
 * Error boundary that catches errors from the GlobalThemeProvider query
 * and provides a fallback with default theme values.
 */
// eslint-disable-next-line @repo/internal/react/no-class-components
class GlobalThemeErrorBoundary extends Component<
	{ children: ReactNode; underlyingComponent: ReactNode },
	{ hasError: boolean }
> {
	constructor(props: { children: ReactNode; underlyingComponent: ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Log the error for debugging purposes
		// eslint-disable-next-line no-console
		console.error(
			'GlobalThemeProvider: Failed to fetch global theme preferences',
			error,
			errorInfo,
		);
	}

	// Memoize the fallback context value
	private fallbackContextValue = { theme: DEFAULT_THEME };

	render() {
		if (this.state.hasError) {
			// Provide a fallback with default theme when query fails
			return (
				<GlobalThemeContext.Provider value={this.fallbackContextValue}>
					{/* Render the underlying component without the GlobalThemeProviderInternal component */}
					{this.props.underlyingComponent}
				</GlobalThemeContext.Provider>
			);
		}

		// Render all children including the GlobalThemeProviderInternal component
		return this.props.children;
	}
}

/**
 * Internal component that performs the query.
 * Separated from the main provider so error boundary can catch query failures.
 */
const GlobalThemeProviderInternal = ({
	accountId,
	children,
}: Omit<GlobalThemeProviderProps, 'accountId'> & { accountId: string }) => {
	const data = useLazyLoadQuery<GlobalThemeProviderQueryType>(
		graphql`
			query globalThemeProviderQuery($accountId: ID!) {
				settings_userPreferencesGlobal(accountId: $accountId) {
					theme
				}
			}
		`,
		{
			accountId,
		},
	);

	const themeString = data?.settings_userPreferencesGlobal?.theme;

	// Memoize the theme object based on the themeString to prevent unnecessary re-renders
	// updateThemeWithDefaultValues creates a new object every time, so we need to memoize it
	const accountTheme = useMemo(
		() => updateThemeWithDefaultValues(themeString ?? ''),
		[themeString],
	);

	// Memoize context value to prevent unnecessary re-renders
	const contextValue = useMemo(() => ({ theme: accountTheme }), [accountTheme]);

	return <GlobalThemeContext.Provider value={contextValue}>{children}</GlobalThemeContext.Provider>;
};

// Shared fallback context value to prevent re-renders
const FALLBACK_CONTEXT_VALUE = { theme: undefined };

/**
 * Provider component that fetches global (account-level) user theme from AGG
 * and exposes it to children via useGlobalTheme hook.
 *
 * This component works only when the 'shared-user-preferences' feature flag is enabled.
 * When the flag is disabled, it will not fetch the theme and useGlobalTheme will return undefined.
 *
 * If the query fails, the provider will gracefully fall back to the default theme ('auto')
 * and log an error to the console.
 */
export const GlobalThemeProvider = ({
	accountId,
	children,
}: GlobalThemeProviderProps): React.JSX.Element => {
	// Skip query execution if accountId is null or undefined or feature flag is disabled
	const shouldSkip = !accountId || !fg('shared-user-preferences');

	if (shouldSkip) {
		// Don't execute query at all - just provide undefined theme
		return (
			<GlobalThemeContext.Provider value={FALLBACK_CONTEXT_VALUE}>
				{children}
			</GlobalThemeContext.Provider>
		);
	}

	return (
		<GlobalThemeErrorBoundary underlyingComponent={children}>
			<GlobalThemeProviderInternal accountId={accountId}>{children}</GlobalThemeProviderInternal>
		</GlobalThemeErrorBoundary>
	);
};

/**
 * Hook to access the global (account-level) user theme.
 *
 * This hook must be used within a GlobalThemeProvider.
 * Returns the full theme state object from AGG, or undefined if the feature flag
 * is disabled or theme hasn't been fetched yet.
 *
 * @returns The global user theme state object or undefined
 */
export const useGlobalTheme = (): Partial<ThemeState> | undefined => {
	const context = useContext(GlobalThemeContext);

	// If feature flag is disabled, return undefined
	if (!fg('shared-user-preferences')) {
		return undefined;
	}

	if (context === undefined) {
		throw new Error('useGlobalTheme must be used within a GlobalThemeProvider');
	}

	return context.theme;
};

/**
 * Hook to access the global (account-level) color mode.
 *
 * This hook must be used within a GlobalThemeProvider.
 * Returns the user's color mode preference from AGG, defaulting to 'auto' if not set.
 * Returns undefined if the feature flag is disabled.
 *
 * @returns The global user color mode (defaults to 'auto' when feature flag is enabled, undefined otherwise)
 */
export const useGlobalColorMode = (): ThemeColorModes | undefined => {
	const context = useContext(GlobalThemeContext);

	// If feature flag is disabled, return undefined
	if (!fg('shared-user-preferences')) {
		return undefined;
	}

	if (context === undefined) {
		throw new Error('useGlobalColorMode must be used within a GlobalThemeProvider');
	}

	// If feature flag is enabled, return colorMode or default to 'auto'
	return context.theme?.colorMode;
};
